import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createEntityQuery } from '@/data/createEntityQuery';
import { actsEntity } from '@/data/entities/acts';
import { fountainScenesEntity } from '@/data/entities/fountainScenes';
import { fragmentsEntity } from '@/data/entities/fragments';
import { PROJECTS_ROOT, projectsEntity } from '@/data/entities/projects';
import { useVersion } from '@/hooks';
import { useOptimisticMutation } from '@/hooks/optimisticHelpers';
import type { CreateFountainScene, IFountainAct, IFountainFragment, IFountainScene } from '@/types';
import { api } from './internal/endpoints';

// Variable types for fountain mutations
type CreateActVariables = {
    projectId: number;
    data: { episode_id?: number; code?: string; title: string; order?: number };
};
type CreateFragmentVariables = {
    projectId: number;
    data: { act_id: number; code?: string; title: string; order?: number };
};
type CreateSceneVariables = {
    projectId: number;
    data: CreateFountainScene;
    /** Caller-supplied predictions for the optimistic entity so the editor can
     *  activate it (and predict its display code) before the backend responds. */
    optimistic?: {
        id: number;
        /** Predicted backend-style scene code (e.g. "1-1-3"). */
        code?: string;
        /** Predicted scene number within the fragment. */
        number?: number;
    };
};
type UpdateActVariables = {
    projectId: number;
    actId: number;
    data: Partial<{
        episode_id: number;
        code: string;
        title: string;
        order: number;
    }>;
};
type UpdateFragmentVariables = {
    projectId: number;
    fragmentId: number;
    data: Partial<{ act_id: number; code: string; title: string; order: number }>;
};

// Query hooks created from entity definitions
const _useProjectActs = createEntityQuery(actsEntity, 'list');
const _useProjectFragments = createEntityQuery(fragmentsEntity, 'list');
const _useProjectScenes = createEntityQuery(fountainScenesEntity, 'list');

// Query: Get project acts
export const useProjectActs = (projectId: number, version?: string, enabled = true) =>
    _useProjectActs({ projectId, version }, { profile: 'editor', enabled: enabled && !!projectId });

// Query: Get project fragments
export const useProjectFragments = (projectId: number, version?: string, enabled = true) =>
    _useProjectFragments({ projectId, version }, { profile: 'editor', enabled: enabled && !!projectId });

// Query: Get project scenes (Fountain V2)
export const useProjectScenes = (projectId: number, version?: string, enabled = true) =>
    _useProjectScenes({ projectId, version }, { profile: 'editor', enabled: enabled && !!projectId });

// Mutation: Create fountain act with optimistic update
export const useCreateFountainAct = () => {
    const version = useVersion();
    return useOptimisticMutation<IFountainAct, CreateActVariables, IFountainAct>({
        mutationFn: ({ projectId, data }) => api.createFountainAct(projectId, data),
        mutationType: 'create',
        getPrimaryQueryKey: ({ projectId }) => actsEntity.fetchers.list.key({ projectId, version }),
        getQueryKeys: ({ projectId }) => [
            actsEntity.fetchers.list.key({ projectId, version }),
            projectsEntity.fetchers.detail.key({ projectId, version }),
        ],
        createOptimisticEntity: ({ projectId, data }) => ({
            id: -Date.now(),
            project_id: projectId,
            episode_id: data.episode_id || null,
            code: data.code || '',
            title: data.title,
            order: data.order || 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            entity_tag: null,
            text_atoms: [],
            tags: [],
        }),
    });
};

// Mutation: Create fountain fragment with optimistic update
export const useCreateFountainFragment = () => {
    const version = useVersion();
    return useOptimisticMutation<IFountainFragment, CreateFragmentVariables, IFountainFragment>({
        mutationFn: ({ projectId, data }) => api.createFountainFragment(projectId, data),
        mutationType: 'create',
        getPrimaryQueryKey: ({ projectId }) => fragmentsEntity.fetchers.list.key({ projectId, version }),
        getQueryKeys: ({ projectId }) => [fragmentsEntity.fetchers.list.key({ projectId, version })],
        createOptimisticEntity: ({ projectId, data }) => ({
            id: -Date.now(),
            project_id: projectId,
            act_id: data.act_id,
            code: data.code || '',
            scenes_qty: 0,
            order: data.order || 1,
            scenes: [],
            entity_tag: null,
            text_atoms: [],
            tags: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        }),
    });
};

// Mutation: Create fountain scene with optimistic update
export const useCreateFountainScene = () => {
    const version = useVersion();
    return useOptimisticMutation<IFountainScene, CreateSceneVariables, IFountainScene>({
        mutationFn: ({ projectId, data }) => api.createFountainScene(projectId, data),
        mutationType: 'create',
        getPrimaryQueryKey: ({ projectId }) => fountainScenesEntity.fetchers.list.key({ projectId, version }),
        getQueryKeys: ({ projectId }) => [
            fountainScenesEntity.fetchers.list.key({ projectId, version }),
            fragmentsEntity.fetchers.list.key({ projectId, version }),
        ],
        getAdditionalQueryKeys: ({ projectId }) => [
            {
                queryKey: fragmentsEntity.fetchers.list.key({ projectId, version }),
                rollbackKey: 'fragments',
            },
        ],
        createOptimisticEntity: ({ projectId, data, optimistic }) => {
            const optimisticSceneId = optimistic?.id ?? -Date.now();
            return {
                id: optimisticSceneId,
                project_id: projectId,
                episode_id: data.episode_id,
                fragment_id: data.fragment_id,
                act: 1,
                position: data.position || 1000,
                number: optimistic?.number ?? 0,
                code: optimistic?.code,
                sequences: [],
                tags: [],
                text_atoms:
                    data.text_atoms?.map((ta, idx) => ({
                        id: -Date.now() - idx - 1,
                        content: ta.content,
                        text_atom_type_id: ta.text_atom_type_id,
                        project_id: projectId,
                        order: idx,
                        textable_id: optimisticSceneId,
                        textable_type: 'scene' as const,
                        tags: [],
                    })) || [],
                assets: [],
                episode: { id: data.episode_id, code: '', title: '', order: 0 },
                scenary_day: null,
                comments_count: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            };
        },
        updateAdditionalCaches: (queryClient, { projectId, data }) => {
            // Optimistically renumber the target fragment's scenes by position, mirroring the
            // backend's renumber-on-create (`code = fragment.code + '-' + rank`, 1-based by
            // position order). The new optimistic scene is already in the scenes cache at this
            // point. Without this the inserted scene shows a stale `floor(position/1000)`-derived
            // number that can duplicate a sibling's code until the refetch lands.
            const fragments = queryClient.getQueryData<IFountainFragment[]>(fragmentsEntity.fetchers.list.key({ projectId, version }));
            const fragmentCode = fragments?.find(f => f.id === data.fragment_id)?.code;
            if (fragmentCode) {
                queryClient.setQueryData<IFountainScene[]>(fountainScenesEntity.fetchers.list.key({ projectId, version }), old => {
                    if (!old) return old;
                    const rankById = new Map<number, number>();
                    old.filter(s => s.fragment_id === data.fragment_id)
                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                        .forEach((s, idx) => {
                            rankById.set(s.id, idx + 1);
                        });
                    return old.map(s => {
                        const rank = rankById.get(s.id);
                        return rank === undefined ? s : { ...s, number: rank, code: `${fragmentCode}-${rank}` };
                    });
                });
            }

            // Update fragment's scenes_qty
            queryClient.setQueryData<IFountainFragment[]>(fragmentsEntity.fetchers.list.key({ projectId, version }), old => {
                if (!old) return old;
                return old.map(fragment =>
                    fragment.id === data.fragment_id ? { ...fragment, scenes_qty: (fragment.scenes_qty || 0) + 1 } : fragment,
                );
            });
        },
    });
};

// Mutation: Update fountain act with optimistic update
export const useUpdateFountainAct = () => {
    const version = useVersion();
    return useOptimisticMutation<IFountainAct, UpdateActVariables, IFountainAct>({
        mutationFn: ({ projectId, actId, data }) => api.updateFountainAct(projectId, actId, data),
        mutationType: 'update',
        getPrimaryQueryKey: ({ projectId }) => actsEntity.fetchers.list.key({ projectId, version }),
        getQueryKeys: ({ projectId }) => [actsEntity.fetchers.list.key({ projectId, version })],
        getEntityId: ({ actId }) => actId,
        updateEntity: (entity, { data }) => ({
            ...entity,
            ...data,
            updated_at: new Date().toISOString(),
        }),
    });
};

// Mutation: Update fountain fragment with optimistic update
export const useUpdateFountainFragment = () => {
    const version = useVersion();
    return useOptimisticMutation<IFountainFragment, UpdateFragmentVariables, IFountainFragment>({
        mutationFn: ({ projectId, fragmentId, data }) => api.updateFountainFragment(projectId, fragmentId, data),
        mutationType: 'update',
        getPrimaryQueryKey: ({ projectId }) => fragmentsEntity.fetchers.list.key({ projectId, version }),
        getQueryKeys: ({ projectId }) => [fragmentsEntity.fetchers.list.key({ projectId, version })],
        getEntityId: ({ fragmentId }) => fragmentId,
        updateEntity: (entity, { data }) => ({
            ...entity,
            ...data,
            updated_at: new Date().toISOString(),
        }),
    });
};

// Mutation: Delete fountain act with optimistic update
export const useDeleteFountainAct = () => {
    const queryClient = useQueryClient();
    const version = useVersion();

    return useMutation({
        mutationFn: ({ projectId, actId }: { projectId: number; actId: number }) => api.deleteFountainAct(projectId, actId),
        onMutate: async ({ projectId, actId }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: actsEntity.fetchers.list.key({ projectId, version }),
            });
            await queryClient.cancelQueries({
                queryKey: fragmentsEntity.fetchers.list.key({ projectId, version }),
            });
            await queryClient.cancelQueries({
                queryKey: fountainScenesEntity.fetchers.list.key({ projectId, version }),
            });

            // Snapshot the previous values
            const previousActs = queryClient.getQueryData<IFountainAct[]>(actsEntity.fetchers.list.key({ projectId, version }));
            const previousFragments = queryClient.getQueryData<IFountainFragment[]>(
                fragmentsEntity.fetchers.list.key({ projectId, version }),
            );
            const previousScenes = queryClient.getQueryData<IFountainScene[]>(
                fountainScenesEntity.fetchers.list.key({ projectId, version }),
            );

            // Optimistically remove the act
            queryClient.setQueryData<IFountainAct[]>(actsEntity.fetchers.list.key({ projectId, version }), old => {
                if (!old) return old;
                return old.filter(act => act.id !== actId);
            });

            // Optimistically remove fragments belonging to this act
            queryClient.setQueryData<IFountainFragment[]>(fragmentsEntity.fetchers.list.key({ projectId, version }), old => {
                if (!old) return old;
                return old.filter(fragment => fragment.act_id !== actId);
            });

            // Optimistically remove scenes belonging to fragments of this act
            const actFragmentIds = previousFragments?.filter(f => f.act_id === actId).map(f => f.id) || [];
            queryClient.setQueryData<IFountainScene[]>(fountainScenesEntity.fetchers.list.key({ projectId, version }), old => {
                if (!old) return old;
                return old.filter(scene => !actFragmentIds.includes(scene.fragment_id || 0));
            });

            return { previousActs, previousFragments, previousScenes, projectId };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousActs) {
                queryClient.setQueryData(actsEntity.fetchers.list.key({ projectId: context.projectId, version }), context.previousActs);
            }
            if (context?.previousFragments) {
                queryClient.setQueryData(
                    fragmentsEntity.fetchers.list.key({ projectId: context.projectId, version }),
                    context.previousFragments,
                );
            }
            if (context?.previousScenes) {
                queryClient.setQueryData(
                    fountainScenesEntity.fetchers.list.key({ projectId: context.projectId, version }),
                    context.previousScenes,
                );
            }
        },
        onSettled: (_result, _error, variables) => {
            // Always invalidate after mutation settles to ensure fresh data
            queryClient.invalidateQueries({
                queryKey: actsEntity.fetchers.list.key({ projectId: variables.projectId, version }),
            });
            queryClient.invalidateQueries({
                queryKey: fragmentsEntity.fetchers.list.key({ projectId: variables.projectId, version }),
            });
            queryClient.invalidateQueries({
                queryKey: fountainScenesEntity.fetchers.list.key({ projectId: variables.projectId, version }),
            });
            // Invalidate project to update act_quantity
            queryClient.invalidateQueries({ queryKey: PROJECTS_ROOT });
        },
    });
};

// Mutation: Delete fountain fragment with optimistic update
export const useDeleteFountainFragment = () => {
    const queryClient = useQueryClient();
    const version = useVersion();

    return useMutation({
        mutationFn: ({ projectId, fragmentId }: { projectId: number; fragmentId: number }) =>
            api.deleteFountainFragment(projectId, fragmentId),
        onMutate: async ({ projectId, fragmentId }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: fragmentsEntity.fetchers.list.key({ projectId, version }),
            });
            await queryClient.cancelQueries({
                queryKey: fountainScenesEntity.fetchers.list.key({ projectId, version }),
            });

            // Snapshot the previous values
            const previousFragments = queryClient.getQueryData<IFountainFragment[]>(
                fragmentsEntity.fetchers.list.key({ projectId, version }),
            );
            const previousScenes = queryClient.getQueryData<IFountainScene[]>(
                fountainScenesEntity.fetchers.list.key({ projectId, version }),
            );

            // Optimistically remove the fragment
            queryClient.setQueryData<IFountainFragment[]>(fragmentsEntity.fetchers.list.key({ projectId, version }), old => {
                if (!old) return old;
                return old.filter(fragment => fragment.id !== fragmentId);
            });

            // Optimistically remove scenes belonging to this fragment
            queryClient.setQueryData<IFountainScene[]>(fountainScenesEntity.fetchers.list.key({ projectId, version }), old => {
                if (!old) return old;
                return old.filter(scene => scene.fragment_id !== fragmentId);
            });

            return { previousFragments, previousScenes, projectId };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousFragments) {
                queryClient.setQueryData(
                    fragmentsEntity.fetchers.list.key({ projectId: context.projectId, version }),
                    context.previousFragments,
                );
            }
            if (context?.previousScenes) {
                queryClient.setQueryData(
                    fountainScenesEntity.fetchers.list.key({ projectId: context.projectId, version }),
                    context.previousScenes,
                );
            }
        },
        onSettled: (_result, _error, variables) => {
            // Always invalidate after mutation settles to ensure fresh data
            queryClient.invalidateQueries({
                queryKey: fragmentsEntity.fetchers.list.key({ projectId: variables.projectId, version }),
            });
            queryClient.invalidateQueries({
                queryKey: fountainScenesEntity.fetchers.list.key({ projectId: variables.projectId, version }),
            });
        },
    });
};
